import { compact, sum } from '@quromedical/utils';

import * as Scale from '../scales';
import { news2RiskScale } from '../scales';
import {
  type Consciousness,
  NEWS2ClinicalRisk,
  type NEWS2Result,
  type NEWS2Score,
  type NEWS2VitalParameterScores,
  type NEWS2VitalParameters,
  OxygenSaturationScale,
  type QuroScoreParameters,
  type QuroScoreResult,
  type QuroVitalParameterScores,
  type VitalParameterScale,
  type VitalParameterScore,
} from '../types';

const consciousnessScore: Record<Consciousness, VitalParameterScore> = {
  alert: 0,
  confusion: 3,
  pain: 3,
  unresponsive: 3,
  verbal: 3,
};

/**
 * Represents the `Air or Oxygen` question in the NEWS2 Chart
 */
const oxygenSaturationScaleScore: Record<
  OxygenSaturationScale,
  VitalParameterScore
> = {
  [OxygenSaturationScale.Scale1]: 0,
  [OxygenSaturationScale.Scale2Air]: 0,
  [OxygenSaturationScale.Scale2Oxygen]: 2,
};

const oxygenSaturationScales: Record<
  OxygenSaturationScale,
  VitalParameterScale
> = {
  [OxygenSaturationScale.Scale1]: Scale.spo2Scale1,
  [OxygenSaturationScale.Scale2Air]: Scale.spo2Scale2Air,
  [OxygenSaturationScale.Scale2Oxygen]: Scale.spo2Scale2Oxygen,
};

const flattenScores = (
  scores: Partial<NEWS2VitalParameterScores>,
): VitalParameterScore[] => {
  const keys = Object.keys(scores) as (keyof NEWS2VitalParameterScores)[];

  const parameterScores = keys.map((key) => {
    const parameterValue = scores[key];

    if (typeof parameterValue === 'number') {
      return parameterValue;
    }

    return parameterValue?.value;
  });

  return compact(parameterScores);
};

/**
 * If there are any risks of level 3 then the lowest possible risk level is `low-medium`
 */
const getMinimumAllowedRisk = (
  scores: Partial<NEWS2VitalParameterScores>,
): NEWS2ClinicalRisk => {
  const parameterScores = flattenScores(scores);

  const hasAnyHigh = parameterScores.some((val) => val === 3);

  return hasAnyHigh ? NEWS2ClinicalRisk.LowMedium : NEWS2ClinicalRisk.Low;
};

const comparativeRiskValue: Record<NEWS2ClinicalRisk, number> = {
  [NEWS2ClinicalRisk.Low]: 1,
  [NEWS2ClinicalRisk.LowMedium]: 2,
  [NEWS2ClinicalRisk.Medium]: 3,
  [NEWS2ClinicalRisk.High]: 4,
};

const getMaxRisk = (
  a: NEWS2ClinicalRisk,
  b: NEWS2ClinicalRisk,
): NEWS2ClinicalRisk => {
  const aComparative = comparativeRiskValue[a];
  const bComparative = comparativeRiskValue[b];

  return aComparative > bComparative ? a : b;
};

const getRisk = (
  total: NEWS2Score,
  scores: Partial<NEWS2VitalParameterScores>,
): NEWS2ClinicalRisk => {
  const lowestRisk = getMinimumAllowedRisk(scores);

  const scoreRisk = news2RiskScale(total);

  return getMaxRisk(lowestRisk, scoreRisk);
};

const getScore = (scores: Partial<NEWS2VitalParameterScores>): number => {
  const parameterScores = flattenScores(scores);

  return sum(parameterScores);
};

export const calculateNEWS2Score = (
  params: NEWS2VitalParameters,
): NEWS2Result => {
  const consciousness = consciousnessScore[params.consciousness];
  const oxygenSaturationScale =
    oxygenSaturationScaleScore[params.oxygenSaturationScale];
  const oxygenSaturation = oxygenSaturationScales[params.oxygenSaturationScale](
    params.oxygenSaturation,
  );
  const pulseRate = Scale.pulseRate(params.pulseRate);
  const respirationRate = Scale.respirationRate(params.respirationRate);
  const systolicBloodPressure = Scale.systolicBloodPressure(
    params.systolicBloodPressure,
  );
  const temperature = Scale.temperature(params.temperature);

  const parameterScores: NEWS2VitalParameterScores = {
    consciousness,
    oxygenSaturationScale,
    oxygenSaturation,
    pulseRate,
    respirationRate,
    systolicBloodPressure,
    temperature,
  };

  const score = getScore(parameterScores);
  const risk = getRisk(score, parameterScores);

  return {
    parameterScores,
    risk,
    score,
  };
};

/**
 * Quro medical score. Represents a partial NEWS2 score calculated based on the sum of individual
 * NEWS2 parameter scores added together
 */
export const calculateQuroScore = (
  params: QuroScoreParameters,
): QuroScoreResult => {
  const {
    oxygenSaturation: oxygenSaturationValue,
    systolicBloodPressure: systolicBloodPressureValue,
  } = params;

  const consciousness = consciousnessScore[params.consciousness];
  const oxygenSaturationScale =
    oxygenSaturationScaleScore[params.oxygenSaturationScale];

  const oxygenSaturation = oxygenSaturationValue
    ? oxygenSaturationScales[params.oxygenSaturationScale](
        oxygenSaturationValue,
      )
    : undefined;

  const pulseRate = Scale.pulseRate(params.pulseRate);
  const respirationRate = Scale.respirationRate(params.respirationRate);

  const systolicBloodPressure = systolicBloodPressureValue
    ? Scale.systolicBloodPressure(systolicBloodPressureValue)
    : undefined;

  const temperature = Scale.temperature(params.temperature);

  const parameterScores: QuroVitalParameterScores = {
    consciousness,
    oxygenSaturationScale,
    oxygenSaturation,
    pulseRate,
    respirationRate,
    systolicBloodPressure,
    temperature,
  };

  const score = getScore(parameterScores);
  const risk = getRisk(score, parameterScores);

  return {
    parameterScores,
    risk,
    score,
  };
};
